import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";

import style from "./Whiteboard.module.css";

@observer
class Whiteboard extends React.Component {
  componentDidMount() {
    this.addSignupScript();
  }

  componentDidUpdate() {
    const links = Array.prototype.slice.call(
      this.discussionElement.querySelectorAll(`.fileLink`)
    );
    links.forEach(link => {
      link.removeEventListener(`click`, this.discussionFileLinkClicked);
      link.addEventListener(`click`, this.discussionFileLinkClicked);
    });
    this.addSignupScript();
  }

  /**
   * Add the signup script tag to the #signup element. The signup element is
   * only visible on the last step, but it always exists.
   */
  addSignupScript() {
    const existingScriptTag = document.querySelector(`#_ck_67407`);
    if (existingScriptTag) {
      return;
    }
    // From ConvertKit:
    // <script async id="_ck_67407" src="https://forms.convertkit.com/67407?v=5"></script>
    const script = document.createElement(`script`);
    script.id = `_ck_67407`;
    script.setAttribute(`src`, `https://forms.convertkit.com/67407?v=5`);
    this.signupElement.appendChild(script);
  }

  discussionFileLinkClicked = event => {
    const filePath = event.target.getAttribute(`data-file-path`);
    if (!filePath) {
      return;
    }
    this.fileLinkClicked(filePath);
  };

  nextClicked = () => {
    this.props.store.setSelectedStepIndex(
      this.props.store.selectedStepIndex + 1
    );
    // Changing tabs doesn't clear the selected file, but clicking
    // 'Next' does. This makes sense to me: you would click tabs to compare
    // files at various steps. When you click Next you want to focus on the
    // discussion in the next step.
    this.props.store.setSelectedFilePath(null);
  };

  fileLinkClicked = filePath => {
    this.props.store.setSelectedFilePath(filePath);
    // Changing the selected file in the tree doesn't scroll us to the top,
    // but changing the selected file with these links does.
    // If you don't scroll to the top in this situation, the user may not see
    // that the selected file has changed because they are too far down.
    window.scrollTo(0, 0);
  };

  renderNextButton() {
    if (this.props.store.onLastStep) {
      return null;
    }
    return (
      <div className={style.nextButtonWrapper}>
        <button className={style.nextButton} onClick={this.nextClicked}>
          Next
        </button>
      </div>
    );
  }

  renderCommands(number, html, then = false) {
    if (!html) {
      return null;
    }
    return (
      <div key={`commands${then}`}>
        <h4>
          {number}. {then ? `Then run these commands` : `Run these commands`}
        </h4>
        <div
          className={style.commands}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }

  renderDiscussion() {
    return (
      <div
        className={style.discussion}
        ref={element => {
          this.discussionElement = element;
        }}
        dangerouslySetInnerHTML={{
          __html: this.props.store.selectedStep.discussion
        }}
      />
    );
  }

  renderDiscussionEnd() {
    const html = this.props.store.selectedStep.discussionEnd;
    if (!html || html.length === 0) {
      return null;
    }
    return (
      <div
        className={style.discussionEnd}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  renderFileList(number, files, titleText) {
    if (files.length === 0) {
      return null;
    }

    return (
      <div key={titleText}>
        <h4>
          {number}. {titleText}
        </h4>
        <div className={style.fileList}>
          {files.map((file, index) => {
            return [
              <span
                className={style.fileListFile}
                onClick={() => {
                  this.fileLinkClicked(file.path);
                }}
              >
                {file.path}
              </span>,
              index <= files.length - 2 ? <span>, </span> : null
            ];
          })}
        </div>
      </div>
    );
  }

  renderFileAdds(number) {
    const addFiles = this.props.store.files.filter(file => {
      return file.manual && file.changeType === `new`;
    });
    return this.renderFileList(number, addFiles, `Add these files`);
  }

  renderFileUpdates(number) {
    const updateFiles = this.props.store.files.filter(file => {
      return file.manual && file.changeType === `updated`;
    });
    return this.renderFileList(number, updateFiles, `Update these files`);
  }

  renderDirections(step) {
    const directions = [
      number => {
        return this.renderCommands(number, step.commandsHtml);
      },
      number => {
        return this.renderFileAdds(number);
      },
      number => {
        return this.renderFileUpdates(number);
      },
      number => {
        return this.renderCommands(number, step.postCommandsHtml, true);
      }
    ];
    let number = 1;

    const directionsElements = [];
    directions.forEach(direction => {
      const element = direction(number);
      if (element) {
        directionsElements.push(element);
        number += 1;
      }
    });

    if (directionsElements.length === 0) {
      return null;
    }

    return (
      <div className={style.steps}>
        <h3>Directions</h3>
        {directionsElements}
      </div>
    );
  }

  renderSignup() {
    const visible = this.props.store.onLastStep;
    const display = visible ? `block` : `none`;
    return (
      <div
        id="signup"
        ref={element => {
          this.signupElement = element;
        }}
        style={{ display }}
      />
    );
  }

  renderMobileWarning() {
    return (
      <div className={style.mobileWarning}>
        <b>Warning:</b> This tutorial was not designed for smaller screens and
        may not be formatted well.
      </div>
    );
  }

  render() {
    const step = this.props.store.selectedStep;
    return (
      <div className={style.whiteboard}>
        <h2>{step.title}</h2>
        {this.renderMobileWarning()}
        {this.renderDiscussion()}
        {this.renderDirections(step)}
        {this.renderDiscussionEnd()}
        {this.renderNextButton()}
        {this.renderSignup()}
      </div>
    );
  }
}
Whiteboard.propTypes = {
  store: PropTypes.object.isRequired
};
export default Whiteboard;



// WEBPACK FOOTER //
// ./src/byos/component/Whiteboard.js